/**
 * i18n.js
 * 
 * Internationalization configuration for HemaAI V2.0
 * Supports: English, French, Portuguese, Arabic, Swahili
 * 
 * @author Dr. Omokayode Aderolu
 * @company OmniAlera Technologies Holdings Inc.
 * @version 2.0.0
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Import translations
import en from '@/locales/en/translation.json';
import fr from '@/locales/fr/translation.json';
import pt from '@/locales/pt/translation.json';
import ar from '@/locales/ar/translation.json';
import sw from '@/locales/sw/translation.json';

const resources = {
  en: { translation: en },
  fr: { translation: fr },
  pt: { translation: pt },
  ar: { translation: ar },
  sw: { translation: sw }
};

/**
 * Supported languages. `dir` drives right-to-left layout for Arabic.
 * Exported for the LanguageSelector and any locale-aware UI.
 */
export const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr', flag: '🇬🇧' },
  { code: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr', flag: '🇫🇷' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', dir: 'ltr', flag: '🇵🇹' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', dir: 'ltr', flag: '🇰🇪' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl', flag: '🇸🇦' },
];

/** Text direction for a language code (defaults to ltr). */
export const getLanguageDir = (code) =>
  (LANGUAGES.find((l) => l.code === code) || LANGUAGES[0]).dir;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: import.meta.env.DEV,
    
    interpolation: {
      escapeValue: false
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'hemaai-language'
    },

    react: {
      useSuspense: true
    }
  });

export default i18n;
